import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, CartesianGrid, XAxis, YAxis, Tooltip } from "recharts";
import Layout from "../components/Layout";
import BodyPainScanner from "../components/BodyPainScanner";
import { api } from "../lib/api";
import { BODY_REGIONS, STEPS, intensityColor, intensityLabel } from "../data/courseData";

const ALL_REGIONS = [...BODY_REGIONS.front, ...BODY_REGIONS.back];
const labelFor = (id) => ALL_REGIONS.find((r) => r.id === id)?.label || id;

export default function PainScanPage() {
  const { step } = useParams();
  const stepNum = parseInt(step, 10);
  const navigate = useNavigate();
  const [value, setValue] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedScan, setSavedScan] = useState(null); // becomes truthy after save
  const [allScans, setAllScans] = useState([]); // chronological history

  useEffect(() => {
    api.get("/pain-scans").then((r) => {
      const scans = r.data.slice().sort((a, b) => a.created_at.localeCompare(b.created_at));
      setAllScans(scans);
    });
  }, [stepNum]);

  const previous = allScans.filter((s) => s.step < stepNum).pop() || null;

  async function save() {
    setSaving(true);
    try {
      const regions = Object.entries(value).map(([region, intensity]) => ({ region, intensity }));
      const { data } = await api.post("/pain-scans", { step: stepNum, regions });
      setSavedScan(data);
      // Refresh history so the new scan is included
      const r = await api.get("/pain-scans");
      setAllScans(r.data.slice().sort((a, b) => a.created_at.localeCompare(b.created_at)));
    } finally { setSaving(false); }
  }

  return (
    <Layout hideNav>
      <button onClick={() => navigate(`/journey/${stepNum}`)} className="text-sm text-[#9AAEC6] flex items-center gap-1 mb-4" data-testid="back-to-step">
        <ArrowLeft size={16} /> Week {stepNum}
      </button>

      {!savedScan ? (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="space-y-5">
          <div>
            <div className="bps-eyebrow">Body Pain Scan</div>
            <h1 className="font-display text-3xl font-light tracking-tighter mt-1">Map the pain.</h1>
            <p className="text-sm text-[#9AAEC6] mt-2">Tap each area where you feel tension. Set its intensity. This builds a visual record over time.</p>
          </div>

          {previous && (
            <div className="bps-card p-4 text-xs text-[#9AAEC6] flex items-center gap-3" data-testid="previous-scan-hint">
              <div className="w-2 h-2 rounded-full bg-[#22D3EE]" />
              Last scan (Week {previous.step}): {previous.regions.length} areas · avg intensity {previous.average_intensity}
            </div>
          )}

          <BodyPainScanner value={value} onChange={setValue} />

          <button onClick={save} disabled={saving || Object.keys(value).length === 0} data-testid="save-scan" className="w-full py-4 rounded-full bg-[#22D3EE] text-[#03101F] font-semibold shadow-[0_0_24px_rgba(34,211,238,0.35)] disabled:opacity-30 transition-all hover:-translate-y-0.5">
            {saving ? "Saving…" : "Save scan"}
          </button>
        </motion.div>
      ) : (
        <ScanSummary savedScan={savedScan} allScans={allScans} stepNum={stepNum} onContinue={() => navigate(`/journey/${stepNum}`)} />
      )}
    </Layout>
  );
}

function ScanSummary({ savedScan, allScans, stepNum, onContinue }) {
  const priorScans = allScans.filter((s) => s.id !== savedScan.id);
  const priorLast = priorScans[priorScans.length - 1] || null;

  const currentAvg = savedScan.average_intensity || 0;
  const priorAvg = priorLast?.average_intensity ?? null;
  const delta = priorAvg != null ? +(currentAvg - priorAvg).toFixed(1) : null;

  // Sort current regions by intensity desc
  const sortedRegions = [...savedScan.regions].sort((a, b) => b.intensity - a.intensity);

  // Region-level deltas vs previous scan
  const priorMap = new Map((priorLast?.regions || []).map((r) => [r.region, r.intensity]));

  // Trend chart series — all scans incl this one
  const trendSeries = allScans.map((s, i) => ({
    label: `S${s.step}.${allScans.filter((x) => x.step === s.step && x.created_at <= s.created_at).length}`,
    avg: s.average_intensity,
    areas: s.regions.length,
    isCurrent: s.id === savedScan.id,
  }));

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="space-y-5 pb-6" data-testid="scan-summary">
      <div className="text-center">
        <div className="mx-auto w-14 h-14 rounded-full bg-[#10B981] flex items-center justify-center mb-3" style={{ boxShadow: "0 0 24px rgba(16,185,129,0.5)" }}>
          <Check size={24} className="text-white" strokeWidth={2.2} />
        </div>
        <div className="bps-eyebrow">Scan saved · Log #{allScans.length}</div>
        <h2 className="font-display text-3xl font-light tracking-tighter mt-2">Your body, on record.</h2>
        <p className="text-sm text-[#9AAEC6] mt-2">Review this entry before you continue. It becomes part of your transformation timeline.</p>
      </div>

      {/* Headline stats */}
      <div className="grid grid-cols-3 gap-3" data-testid="summary-stats">
        <Stat label="Areas" value={savedScan.regions.length} sub={priorLast ? `${diffSign(savedScan.regions.length - priorLast.regions.length)} vs last` : "first scan"} />
        <Stat label="Avg" value={currentAvg.toFixed(1)} sub={delta != null ? `${diffSign(delta)} vs last` : "/10"} color={delta != null ? (delta < 0 ? "#10B981" : delta > 0 ? "#F87171" : "#7C8FA8") : undefined} />
        <Stat label="Peak" value={sortedRegions[0]?.intensity ?? "—"} sub={sortedRegions[0] ? labelFor(sortedRegions[0].region) : "/10"} color={sortedRegions[0] ? intensityColor(sortedRegions[0].intensity) : undefined} />
      </div>

      {/* Region log */}
      <div className="bps-card p-5" data-testid="region-log">
        <div className="bps-eyebrow mb-3">Region log</div>
        <ul className="space-y-2.5">
          {sortedRegions.map((r) => {
            const prior = priorMap.get(r.region);
            const d = prior != null ? r.intensity - prior : null;
            return (
              <li key={r.region} className="flex items-center gap-3 text-sm" data-testid={`region-log-${r.region}`}>
                <span className="w-2 h-8 rounded-full" style={{ background: intensityColor(r.intensity), boxShadow: `0 0 10px ${intensityColor(r.intensity)}99` }} />
                <span className="flex-1 text-white/90">{labelFor(r.region)}</span>
                <span className="font-mono text-xs text-[#9AAEC6]">{intensityLabel(r.intensity)}</span>
                <span className="font-display font-medium text-base w-8 text-right">{r.intensity}</span>
                {d != null && (
                  <span className={`inline-flex items-center gap-0.5 text-[11px] font-mono w-12 justify-end ${d < 0 ? "text-[#10B981]" : d > 0 ? "text-[#F87171]" : "text-[#7C8FA8]"}`}>
                    {d < 0 ? <TrendingDown size={11} /> : d > 0 ? <TrendingUp size={11} /> : <Minus size={11} />}
                    {d > 0 ? "+" : ""}{d}
                  </span>
                )}
                {d == null && prior == null && priorLast && (
                  <span className="text-[10px] font-mono text-[#22D3EE] w-12 text-right">NEW</span>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      {/* History comparison */}
      {priorScans.length > 0 && (
        <div className="bps-card p-5" data-testid="history-comparison">
          <div className="bps-eyebrow mb-3">Compare with prior scans</div>
          <div className="h-40 mb-4">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trendSeries}>
                <defs>
                  <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22D3EE" stopOpacity={0.55} />
                    <stop offset="100%" stopColor="#22D3EE" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="label" stroke="#7C8FA8" fontSize={10} />
                <YAxis stroke="#7C8FA8" fontSize={10} domain={[0, 10]} />
                <Tooltip contentStyle={{ background: "#0A1530", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#F5F7FA" }} />
                <Area type="monotone" dataKey="avg" stroke="#22D3EE" strokeWidth={2} fill="url(#trendGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <ul className="space-y-2" data-testid="prior-scans-list">
            {priorScans.slice().reverse().map((s) => {
              const stepMeta = STEPS.find((x) => x.step === s.step);
              const sDelta = +(currentAvg - s.average_intensity).toFixed(1);
              return (
                <li key={s.id} className="flex items-center gap-3 text-sm py-2 border-b border-white/5 last:border-0">
                  <span className="inline-flex w-2 h-2 rounded-full" style={{ background: stepMeta?.accent || "#22D3EE" }} />
                  <div className="flex-1">
                    <div className="text-white/90 leading-tight">Week {s.step} · {stepMeta?.title}</div>
                    <div className="text-[11px] text-[#7C8FA8]">{new Date(s.created_at).toLocaleDateString()} · {s.regions.length} areas</div>
                  </div>
                  <div className="font-display font-medium text-base">{s.average_intensity}</div>
                  <span className={`text-[11px] font-mono w-14 text-right inline-flex items-center justify-end gap-0.5 ${sDelta < 0 ? "text-[#10B981]" : sDelta > 0 ? "text-[#F87171]" : "text-[#7C8FA8]"}`}>
                    {sDelta < 0 ? <TrendingDown size={11} /> : sDelta > 0 ? <TrendingUp size={11} /> : <Minus size={11} />}
                    {sDelta > 0 ? "+" : ""}{sDelta}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {!priorScans.length && (
        <div className="bps-card p-5 text-center text-sm text-[#9AAEC6]" data-testid="first-scan-note">
          This is your <span className="text-white">baseline</span>. Future scans will compare against this entry — you'll see exactly where your body is changing.
        </div>
      )}

      <button onClick={onContinue} data-testid="continue-after-summary" className="w-full py-4 rounded-full bg-[#22D3EE] text-[#03101F] font-semibold shadow-[0_0_24px_rgba(34,211,238,0.35)] hover:-translate-y-0.5 transition-all">
        Continue to next practice
      </button>
    </motion.div>
  );
}

function Stat({ label, value, sub, color }) {
  return (
    <div className="bps-card p-4">
      <div className="bps-eyebrow">{label}</div>
      <div className="font-display text-2xl font-medium mt-1" style={color ? { color } : undefined}>{value}</div>
      <div className="text-[11px] text-[#7C8FA8] mt-0.5 font-mono">{sub}</div>
    </div>
  );
}

function diffSign(n) {
  if (n == null || Number.isNaN(n)) return "—";
  if (n > 0) return `+${n}`;
  return `${n}`;
}
